import { useEffect, useRef } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import useWorkflowSteps from '~/containers/viewer/explorer-sidebar/WorkflowStepper/useWorkflowSteps';
import { initializeEngine } from '~/engine/Engine';
import { Environment } from '~/engine/data/Environment';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { ToolManager } from '~/engine/tool/ToolManager';
import {
    Orientations,
    PlaneView,
    PlaneViewType,
} from '~/engine/view/views/PlaneView';
import { dicomProcessorState } from '~/state/atoms/dicomProcessorAtoms';
import {
    currentDoseState,
    doseVisibilityState,
} from '~/state/atoms/dose/doseAtoms';
import {
    environmentState,
    lowerSideViewState,
    mainViewState,
    secondaryEnvironmentState,
    secondaryLowerSideViewState,
    secondaryMainViewState,
    secondaryUpperSideViewState,
    upperSideViewState,
    viewManagerState,
} from '~/state/atoms/engineAtoms';
import { patientRepositoryState } from '~/state/atoms/patientAtoms';
import {
    bulkRoiSelectionActiveState,
    selectedROIState,
} from '~/state/atoms/structureSet/roiAtoms';
import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import {
    brushOptionsState,
    deformOptionsState,
    lineDeformOptionsState,
    lineOptionsState,
    registrationModeState,
    selectedToolState,
    transformAllSupportState,
} from '~/state/atoms/toolsAtoms';
import {
    expandDoseStatisticsTableState,
    GridLayoutMode,
    gridLayoutModeState,
    showDoseStatisticsTableFullScreenState,
    showDoseStatisticsTableState,
    showDVHViewComponentFullScreenState,
    showDVHViewComponentState,
    viewportOrientationOrderState,
} from '~/state/atoms/viewerAtoms';

export function useViewportLayout() {
    const patientRepository = useRecoilValue(patientRepositoryState);
    const dicomProcessor = useRecoilValue(dicomProcessorState);
    const currentDose = useRecoilValue(currentDoseState);
    const [environment, setEnvironment] = useRecoilState(environmentState);
    const [secondaryEnvironment] = useRecoilState(secondaryEnvironmentState);
    const [viewManager, setViewManager] = useRecoilState(viewManagerState);

    const [mainView, setMainView] = useRecoilState(mainViewState);
    const [upperSideView, setUpperSideView] =
        useRecoilState(upperSideViewState);
    const [lowerSideView, setLowerSideView] =
        useRecoilState(lowerSideViewState);
    const [secondaryMain, setSecondaryMain] = useRecoilState(
        secondaryMainViewState
    );
    const [secondaryUpperSide, setSecondaryUpperSide] = useRecoilState(
        secondaryUpperSideViewState
    );
    const [secondaryLowerSide, setSecondaryLowerSide] = useRecoilState(
        secondaryLowerSideViewState
    );
    const [brushOptions, setBrushOptions] = useRecoilState(brushOptionsState);
    const [lineDeformOptions, setLineDeformOptions] = useRecoilState(
        lineDeformOptionsState
    );
    const [deformOptions, setDeformOptions] =
        useRecoilState(deformOptionsState);

    const selectedTool = useRecoilValue(selectedToolState);
    const lineOptions = useRecoilValue(lineOptionsState);
    const registrationOptions = useRecoilValue(registrationModeState);
    const selectedROI = useRecoilValue(selectedROIState);
    const isBulkSelectionActive = useRecoilValue(bulkRoiSelectionActiveState);
    const transformAllSupport = useRecoilValue(transformAllSupportState);

    const gridLayoutMode = useRecoilValue(gridLayoutModeState);
    const viewportOrientationOrder = useRecoilValue(
        viewportOrientationOrderState
    );

    const setShowDVHViewComponent = useSetRecoilState(
        showDVHViewComponentState
    );
    const setShowDVHViewComponentFullScreen = useSetRecoilState(
        showDVHViewComponentFullScreenState
    );
    const setShowDoseStatisticsTable = useSetRecoilState(
        showDoseStatisticsTableState
    );
    const setExpandDoseStatisticsTable = useSetRecoilState(
        expandDoseStatisticsTableState
    );
    const setShowDoseStatisticsTableFullScreen = useSetRecoilState(
        showDoseStatisticsTableFullScreenState
    );
    const setDoseVisibility = useSetRecoilState(doseVisibilityState);
    const { currentStep } = useWorkflowSteps();
    const isOnSetupStep = currentStep?.id === 'setup';
    const isOnDoseStep = currentStep?.id === 'predictive-planning';
    const isOnExportStep = currentStep?.id === 'export';

    const viewportLayoutContainer = useRef<HTMLDivElement>(null);

    const primaryViewsRef = useRef<{
        main: PlaneView | null;
        upper: PlaneView | null;
        lower: PlaneView | null;
    }>({ main: null, upper: null, lower: null });

    const secondaryViewsRef = useRef<{
        main: PlaneView | null;
        upper: PlaneView | null;
        lower: PlaneView | null;
    }>({ main: null, upper: null, lower: null });

    const mainCanvas = useRef<HTMLCanvasElement>(null);
    const upperSideCanvas = useRef<HTMLCanvasElement>(null);
    const lowerSideCanvas = useRef<HTMLCanvasElement>(null);
    const mainCanvas2D = useRef<HTMLCanvasElement>(null);
    const upperSideCanvas2D = useRef<HTMLCanvasElement>(null);
    const lowerSideCanvas2D = useRef<HTMLCanvasElement>(null);

    const secondaryMainCanvas = useRef<HTMLCanvasElement>(null);
    const secondaryUpperSideCanvas = useRef<HTMLCanvasElement>(null);
    const secondaryLowerSideCanvas = useRef<HTMLCanvasElement>(null);
    const secondaryMainCanvas2D = useRef<HTMLCanvasElement>(null);
    const secondaryUpperSideCanvas2D = useRef<HTMLCanvasElement>(null);
    const secondaryLowerSideCanvas2D = useRef<HTMLCanvasElement>(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => initializeEnvironment(), []);

    useEffect(() => {
        if (!environment) {
            return;
        }
        setupGridLayout();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [environment, secondaryEnvironment, gridLayoutMode]);

    useEffect(() => {
        const dvhAvailable =
            !!currentDose && gridLayoutMode !== GridLayoutMode.SideBySide;
        const showDose =
            dvhAvailable && (isOnSetupStep || isOnDoseStep || isOnExportStep);

        // Dose overlay on canvas
        setDoseVisibility(showDose);
        environment?.updateDoseVisibility(showDose);

        // DVH & statistics panels: null = disabled, false = hidden, true = shown
        // Only show DVH panels on the dose step, not on the export step
        const showDvhPanels = dvhAvailable && isOnDoseStep;
        if (!dvhAvailable) {
            setShowDVHViewComponent(null);
            setShowDoseStatisticsTable(null);
            setShowDVHViewComponentFullScreen(false);
        } else {
            setShowDVHViewComponent(showDvhPanels);
            setShowDoseStatisticsTable(showDvhPanels);
            // Reset fullscreen state when not showing dose panels
            if (!showDvhPanels) {
                setShowDVHViewComponentFullScreen(false);
            }
        }
        setExpandDoseStatisticsTable(null);
        setShowDoseStatisticsTableFullScreen(null);
    }, [
        currentDose,
        isOnSetupStep,
        isOnDoseStep,
        isOnExportStep,
        gridLayoutMode,
        environment,
        setDoseVisibility,
        setShowDVHViewComponent,
        setShowDoseStatisticsTable,
        setShowDVHViewComponentFullScreen,
        setExpandDoseStatisticsTable,
        setShowDoseStatisticsTableFullScreen,
    ]);

    useEffect(
        () => setSelectedToolForViews(),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            selectedTool,
            brushOptions.mode,
            brushOptions.erase,
            lineOptions,
            registrationOptions,
            viewManager,
            environment?.image,
            selectedROI,
            isBulkSelectionActive,
            viewportOrientationOrder,
            transformAllSupport,
        ]
    );

    const initializeEnvironment = () => {
        if (
            !dicomProcessor ||
            !patientRepository ||
            !viewportLayoutContainer.current
        ) {
            return;
        }

        const vm = initializeEngine(
            dicomProcessor,
            viewportLayoutContainer.current
        );
        setEnvironment(vm.env);
        setViewManager(vm);
    };

    const mountPlaneView = (
        label: Orientations,
        canvas: HTMLCanvasElement,
        canvas2D: HTMLCanvasElement,
        type: PlaneViewType
    ) => {
        if (!viewManager || !environment) {
            return;
        }
        const env: Environment | null =
            type === PlaneViewType.Primary ? environment : secondaryEnvironment;

        if (!env) {
            return;
        }

        const view = new PlaneView(
            env,
            viewManager,
            PlaneView.ORIENTATIONS[label],
            canvas,
            canvas2D,
            label,
            type
        );
        if (PlaneViewType.Primary === type) {
            viewManager.addView(view);
        } else if (PlaneViewType.Secondary === type) {
            viewManager.addSecondaryView(view);
        }

        if (env?.image) {
            view.fitToView(env.image.boundingBox);
        }

        if (type === PlaneViewType.Primary) {
            if (label === viewportOrientationOrder[0]) {
                setMainView(view);
                primaryViewsRef.current.main = view;
            }
            if (label === viewportOrientationOrder[1]) {
                setUpperSideView(view);
                primaryViewsRef.current.upper = view;
            }
            if (label === viewportOrientationOrder[2]) {
                setLowerSideView(view);
                primaryViewsRef.current.lower = view;
            }
        } else {
            if (label === viewportOrientationOrder[0]) {
                setSecondaryMain(view);
                secondaryViewsRef.current.main = view;
            }
            if (label === viewportOrientationOrder[1]) {
                setSecondaryUpperSide(view);
                secondaryViewsRef.current.upper = view;
            }
            if (label === viewportOrientationOrder[2]) {
                setSecondaryLowerSide(view);
                secondaryViewsRef.current.lower = view;
            }
        }
    };

    const setupPrimaryViews = () => {
        if (
            !mainCanvas.current ||
            !upperSideCanvas.current ||
            !lowerSideCanvas.current
        ) {
            return;
        }
        if (
            !mainCanvas2D.current ||
            !upperSideCanvas2D.current ||
            !lowerSideCanvas2D.current
        ) {
            return;
        }

        clearPrimaryViews();

        Array.from({ length: viewportOrientationOrder.length }, (_, idx) =>
            mountPlaneView(
                viewportOrientationOrder[idx],
                [mainCanvas, upperSideCanvas, lowerSideCanvas][idx].current!,
                [mainCanvas2D, upperSideCanvas2D, lowerSideCanvas2D][idx]
                    .current!,
                PlaneViewType.Primary
            )
        );
    };

    const setupSecondaryViews = () => {
        if (!viewManager) {
            return;
        }

        Array.from({ length: viewportOrientationOrder.length }, (_, idx) => {
            const canvas = [
                secondaryMainCanvas,
                secondaryUpperSideCanvas,
                secondaryLowerSideCanvas,
            ][idx].current;
            const canvas2D = [
                secondaryMainCanvas2D,
                secondaryUpperSideCanvas2D,
                secondaryLowerSideCanvas2D,
            ][idx].current;
            if (canvas && canvas2D) {
                mountPlaneView(
                    viewportOrientationOrder[idx],
                    canvas,
                    canvas2D,
                    PlaneViewType.Secondary
                );
            }
        });
    };

    const clearPrimaryViews = () => {
        if (!viewManager) {
            return;
        }
        const refs = primaryViewsRef.current;
        if (refs.main) {
            viewManager.removeView(refs.main);
        }
        if (refs.upper) {
            viewManager.removeView(refs.upper);
        }
        if (refs.lower) {
            viewManager.removeView(refs.lower);
        }
        primaryViewsRef.current = { main: null, upper: null, lower: null };
        setMainView(null);
        setUpperSideView(null);
        setLowerSideView(null);
    };

    const clearSecondaryViews = () => {
        if (!viewManager) {
            return;
        }
        const refs = secondaryViewsRef.current;
        if (refs.main) {
            viewManager.removeSecondaryView(refs.main);
        }
        if (refs.upper) {
            viewManager.removeSecondaryView(refs.upper);
        }
        if (refs.lower) {
            viewManager.removeSecondaryView(refs.lower);
        }
        secondaryViewsRef.current = { main: null, upper: null, lower: null };
        setSecondaryMain(null);
        setSecondaryUpperSide(null);
        setSecondaryLowerSide(null);
    };

    const setupGridLayout = () => {
        setupPrimaryViews();
        gridLayoutMode === GridLayoutMode.SideBySide
            ? setupSecondaryViews()
            : clearSecondaryViews();
        viewManager?.resetViews();
    };

    const setSelectedToolForViews = () => {
        if (
            !viewManager ||
            !environment?.image ||
            !mainView ||
            !upperSideView ||
            !lowerSideView
        ) {
            ToolManager.deactivateAndClearTool();
            return;
        }
        const views = [mainView, upperSideView, lowerSideView];
        const base = {
            panZoom: environment.panZoom,
            views,
            environment: viewManager.env,
        };
        // Tools capture the target structure when armed, so an armed brush
        // would keep writing to the active ROI after bulk selection engages.
        // Arm tools without a structure while bulk mode is on.
        const structure = isBulkSelectionActive
            ? undefined
            : environment.structureSet?.getRoi(selectedROI);

        switch (selectedTool) {
            case 'brush':
                ToolManager.setActiveTool({
                    ...base,
                    tool: 'brush',
                    structure,
                    image: environment.image,
                    options: brushOptions,
                    onBrushSizeChanged: (diameter) =>
                        updateBrushSizeCallback(diameter),
                });
                break;
            case 'line': {
                const viewsInOrder = [mainView, upperSideView, lowerSideView];
                const transversalIdx = viewportOrientationOrder.indexOf(
                    Orientations.TRANSVERSAL_ORIENTATION
                );
                const drawingView =
                    transversalIdx >= 0
                        ? (viewsInOrder[transversalIdx] ?? undefined)
                        : undefined;
                ToolManager.setActiveTool({
                    ...base,
                    tool: 'line',
                    structure,
                    image: environment.image,
                    options: lineOptions,
                    drawingView,
                });
                break;
            }
            case 'registration':
                ToolManager.setActiveTool({
                    ...base,
                    tool: 'registration',
                    mode: registrationOptions.mode,
                });
                break;
            case 'boxZoom':
                ToolManager.setActiveTool({
                    ...base,
                    tool: 'boxZoom',
                });
                break;
            case 'transform': {
                let siblingSupports: RegionOfInterest[] | undefined;
                if (transformAllSupport && structure) {
                    const ssObj = environment.structureSet;
                    const domainRoi = ssObj?.parent.getRoiByNumber(
                        structure.roiNumber
                    );
                    if (
                        ssObj &&
                        domainRoi?.interpretedType ===
                            RoiInterpretedType.SUPPORT
                    ) {
                        siblingSupports = ssObj.structures.filter((s) => {
                            if (s.roiNumber === structure.roiNumber) {
                                return false;
                            }
                            const dr = ssObj.parent.getRoiByNumber(s.roiNumber);
                            return (
                                dr?.interpretedType ===
                                RoiInterpretedType.SUPPORT
                            );
                        });
                    }
                }
                ToolManager.setActiveTool({
                    ...base,
                    tool: 'transform',
                    structure,
                    siblingSupports,
                });
                break;
            }
            case 'lineDeform':
                ToolManager.setActiveTool({
                    ...base,
                    tool: 'lineDeform',
                    structure,
                    image: environment.image,
                    options: lineDeformOptions,
                    onSpreadChanged: (spreadPercent) =>
                        updateLineDeformSpreadCallback(spreadPercent),
                });
                break;
            case 'deform':
                ToolManager.setActiveTool({
                    ...base,
                    tool: 'deform',
                    structure,
                    image: environment.image,
                    options: deformOptions,
                    onDiameterChanged: (diameter) =>
                        updateDeformDiameterCallback(diameter),
                });
                break;
            case 'poi-move':
            case 'pan':
            default:
                ToolManager.setActiveTool({
                    ...base,
                    tool: 'pan',
                });
                break;
        }
    };

    const updateBrushSizeCallback = (diameter: number) => {
        setBrushOptions((options) => ({
            ...options,
            diameter,
        }));
    };

    const updateLineDeformSpreadCallback = (spreadPercent: number) => {
        setLineDeformOptions((options) => ({
            ...options,
            spreadPercent,
        }));
    };

    const updateDeformDiameterCallback = (diameter: number) => {
        setDeformOptions((options) => ({
            ...options,
            diameter,
        }));
    };

    return {
        viewportLayoutContainer,
        gridLayoutMode,
        viewportOrientationOrder,
        main: { canvas: mainCanvas, canvas2D: mainCanvas2D, view: mainView },
        upper: {
            canvas: upperSideCanvas,
            canvas2D: upperSideCanvas2D,
            view: upperSideView,
        },
        lower: {
            canvas: lowerSideCanvas,
            canvas2D: lowerSideCanvas2D,
            view: lowerSideView,
        },
        secondaryMain: {
            canvas: secondaryMainCanvas,
            canvas2D: secondaryMainCanvas2D,
            view: secondaryMain,
        },
        secondaryUpper: {
            canvas: secondaryUpperSideCanvas,
            canvas2D: secondaryUpperSideCanvas2D,
            view: secondaryUpperSide,
        },
        secondaryLower: {
            canvas: secondaryLowerSideCanvas,
            canvas2D: secondaryLowerSideCanvas2D,
            view: secondaryLowerSide,
        },
    };
}
